import { BottomSheet } from '@liveauctioneers/hammer-ui-core/bottomSheet';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { MapPin } from '@liveauctioneers/hammer-ui-core/icons';
import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useState } from 'react';

import { useCatalogPurchaseProtectionEligible } from '@/components/PurchaseProtection/PurchaseProtectionBanner/hooks/useCatalogPurchaseProtectionEligible';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogDescription,
    getCatalogIsDone,
    getCatalogIsTimedPlus,
    getCatalogLongAddress,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { safelyRender } from '@/utils/safelyRender';

import { CatalogDate } from '../Details/components/CatalogDate/CatalogDate';
import { AuctionCuratedBy } from './AuctionCuratedBy';
import { CatalogDetailsZoneMobileDescription } from './CatalogDetailsZoneMobileDescription';
import { CatalogDetailsZoneMobileHeader } from './CatalogDetailsZoneMobileHeader';
import { CatalogDetailsZoneTimedPlusLink } from './CatalogDetailsZoneTimedPlusLink';
import { CatalogPurchaseProtectionLine } from './CatalogPurchaseProtectionLine';

type Props = {
    catalogId: number;
    className?: string;
};

// Mobile "Details Zone" per Figma: same data/scope as the desktop CatalogDetailsZone, sized down
// (smaller auction-house logo) and self-padded, since it renders full-bleed on a mobile viewport
// rather than inside a desktop page grid that already supplies its own gutter. Unlike desktop,
// the Figma mobile card also bakes in the register/enter-auction CTA and registration banner
// (desktop renders those from a separate sidebar component), so this component owns the
// forceIsLive/setForceIsLive pair itself instead of receiving it from a parent.
export const CatalogDetailsZoneMobile = ({ catalogId, className }: Props) => {
    const formatMessage = useAppFormatMessage();
    const [forceIsLive, setForceIsLive] = useState(false);
    const [isDescriptionSheetOpen, setIsDescriptionSheetOpen] = useState(false);
    const description = useAppSelector((state) => getCatalogDescription(state, catalogId));
    const location = useAppSelector((state) => getCatalogLongAddress(state, catalogId));
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const purchaseProtectionEligible = useCatalogPurchaseProtectionEligible(catalogId);
    const timedPlusLink = !isCatalogDone && isTimedPlus ? <CatalogDetailsZoneTimedPlusLink /> : undefined;

    const openDescriptionSheet = () => {
        setIsDescriptionSheetOpen(true);
    };

    const closeDescriptionSheet = () => {
        setIsDescriptionSheetOpen(false);
    };

    return (
        <div
            className={cn('flex flex-col gap-md bg-background-primary p-sm', className)}
            data-testid="catalog-details-zone-mobile"
        >
            <CatalogDetailsZoneMobileHeader
                catalogId={catalogId}
                forceIsLive={forceIsLive}
                setForceIsLive={setForceIsLive}
            />
            <div className="flex flex-col gap-xs">
                <CatalogDate
                    catalogId={catalogId}
                    className="border-none pr-0"
                    forceIsLive={forceIsLive}
                    hideBorders
                    hideStartsOnLabel
                    showAddToCalendar
                    showClosedLabel
                    stackTimedPlusLink
                    timedPlusLink={timedPlusLink}
                    variant="detailsZone"
                />
                <div className="flex items-center gap-xs">
                    <MapPin
                        color="primary"
                        size="sm"
                    />
                    <BodySecondary>{location}</BodySecondary>
                </div>
                <CatalogDetailsZoneMobileDescription
                    catalogId={catalogId}
                    onReadMoreClick={openDescriptionSheet}
                />
                {purchaseProtectionEligible && <CatalogPurchaseProtectionLine className="gap-xs" />}
            </div>
            <AuctionCuratedBy catalogId={catalogId} />
            <BottomSheet
                className="mdMin:hidden"
                footer={
                    <Button
                        className="w-full"
                        onClick={closeDescriptionSheet}
                        style="neutral"
                        variant="transparent"
                    >
                        {formatMessage(['close'])}
                    </Button>
                }
                header={{
                    description: null,
                    title: formatMessage(['catalogPage', 'catalogDetailsZone', 'auctionDescription']),
                }}
                onDismiss={closeDescriptionSheet}
                open={isDescriptionSheetOpen}
            >
                <BodyPrimary className="whitespace-pre-line">{safelyRender({ html: description })}</BodyPrimary>
            </BottomSheet>
        </div>
    );
};
